import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * One website widget on the Web chat page (plan-004 WC-81, FR-WC-16, FR-WC-17, DD4-18).
 *
 * The voice agent that answers it, its state in plain words, the on/off switch, the voice switch,
 * the allowed sites, how to install it, and Disconnect behind a confirmation.
 *
 * HONEST STATE. The four states are the API's (`WebChatWidget.state`), each with the sentence that
 * says what a visitor sees: `answering` ("Connected · answering"), `needs_origin` (on, but no site is
 * listed, so every site is refused — a widget connected before sites had to be listed), `disabled`
 * and `not_connected` (what disconnecting leaves: off, voice off, no sites).
 *
 * THE SWITCHES ASK THE API. Turning the chat or its voice on with no site listed is refused by the
 * API (422 `origin_list_empty`) and the page shows the API's own sentence beside the switch, rather
 * than a console rule that could drift from it. Members and Viewers see the switches disabled with
 * the reason: only Owners and Admins change the website chat (the API refuses anyone else).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Tag, type TagStatus } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { SwitchControl } from "../billing/switch-control.js";
import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import type {
  WebChatInstallView,
  WebsiteWidgetChange,
  WebsiteWidgetState,
  WebsiteWidgetView,
} from "../../api/slice/messaging.js";
import { InstallGuide } from "./install-guide.js";
import { OriginsEditor } from "./origins-editor.js";

/** The words on each state's tag. */
export const WIDGET_STATE_LABEL = {
  get answering() { return portalText("operations", "connected_answering_a1f73db7"); },
  get needs_origin() { return portalText("operations", "on_no_site_listed_a3396344"); },
  get disabled() { return portalText("operations", "disabled_f4f4473d"); },
  get not_connected() { return portalText("operations", "not_connected_8b02f3de"); },
} as const satisfies Record<WebsiteWidgetState, string>;

const WIDGET_STATE_STATUS = {
  answering: "positive",
  needs_origin: "caution",
  disabled: "muted",
  not_connected: "muted",
} as const satisfies Record<WebsiteWidgetState, TagStatus>;

/** What each state means for a visitor, in plain words. */
export const WIDGET_STATE_EXPLAINED = {
  get answering() { return portalText("operations", "it_answers_visitors_on_the_sites_listed_below_7ddab6a3"); },
  get needs_origin() { return portalText("operations", "it_is_switched_on_but_no_site_is_listed_so_it_answers_nowh_c15bf29c"); },
  get disabled() { return portalText("operations", "it_is_switched_off_and_answers_nowhere_the_snippet_on_your_61cad4ca"); },
  get not_connected() { return portalText("operations", "it_was_disconnected_off_voice_off_and_no_sites_listed_its_3a56559c"); },
} as const satisfies Record<WebsiteWidgetState, string>;

export interface WidgetCardProps {
  readonly widget: WebsiteWidgetView;
  readonly install: WebChatInstallView;
  readonly canEdit: boolean;
  readonly refusedBecause: string | null;
  /** Opens the connect form for this widget's voice agent (a disconnected widget comes back there). */
  readonly onReconnect: (voiceAgentId: string) => void;
}

export function WidgetCard({ widget, install, canEdit, refusedBecause, onReconnect }: WidgetCardProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const titleId = `mg-widget-${widget.channelBindingId}`;

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: QUERY_KEY.messagingMedium });

  const change = useMutation({
    mutationFn: (next: WebsiteWidgetChange) => gateway.updateWebChatWidget(widget.channelBindingId, next),
    onSettled: refresh,
  });
  const disconnect = useMutation({
    mutationFn: () => gateway.disconnectWebChatWidget(widget.channelBindingId),
    onSuccess: () => {
      setConfirming(false);
    },
    onSettled: refresh,
  });

  const changing = change.isPending ? change.variables : undefined;
  const refusedTitle = canEdit ? undefined : (refusedBecause ?? undefined);
  const connected = widget.state !== "not_connected";

  return (
    <article className="mg-card mg-widget" aria-labelledby={titleId}>
      <div className="mg-widget-head">
        <h2 className="mg-widget-title" id={titleId}>
          {widget.voiceAgentName}
        </h2>
        <Tag status={WIDGET_STATE_STATUS[widget.state]}>{WIDGET_STATE_LABEL[widget.state]}</Tag>
      </div>
      <p className="mg-widget-state">{WIDGET_STATE_EXPLAINED[widget.state]}</p>

      {connected ? (
        <div className="mg-behaviour">
          <span className="mg-setting">
            <span className="mg-setting-body">
              <span className="mg-setting-name">{portalText("operations", "answer_chats_f6156b14")}</span>
              <span className="mg-setting-dek">
                {portalText("operations", "off_stops_it_at_once_the_bubble_disappears_from_your_site_d5a6b50b")}</span>
            </span>
            <span title={refusedTitle}>
              <SwitchControl
                label={portalText("operations", "answer_chats_for_value_557f9617", { v0: String(widget.voiceAgentName) })}
                labelVisible={false}
                checked={changing?.enabled ?? widget.enabled}
                disabled={!canEdit || change.isPending}
                onChange={(next) => {
                  change.mutate({ enabled: next });
                }}
              />
            </span>
          </span>
          <span className="mg-setting">
            <span className="mg-setting-body">
              <span className="mg-setting-name">{portalText("operations", "voice_in_the_chat_97f0b101")}</span>
              <span className="mg-setting-dek">
                {portalText("operations", "visitors_can_press_talk_and_speak_to_the_agent_voice_minut_4734a56d")}</span>
            </span>
            <span title={refusedTitle}>
              <SwitchControl
                label={portalText("operations", "voice_in_the_chat_for_value_7e31e491", { v0: String(widget.voiceAgentName) })}
                labelVisible={false}
                checked={changing?.voiceEnabled ?? widget.voiceEnabled}
                disabled={!canEdit || change.isPending}
                onChange={(next) => {
                  change.mutate({ voiceEnabled: next });
                }}
              />
            </span>
          </span>
          {change.isError ? (
            <p className="c-note c-note-critical" role="alert">
              {apiErrorText(change.error, portalText("operations", "that_change_was_not_saved_e59edb2b"))}
            </p>
          ) : null}
        </div>
      ) : null}

      {connected ? (
        <OriginsEditor
          key={widget.allowedOrigin.join(" ")}
          widget={widget}
          canEdit={canEdit}
          refusedBecause={refusedBecause}
        />
      ) : null}

      {connected ? <InstallGuide widget={widget} install={install} /> : null}

      {canEdit ? (
        <div className="mg-card-actions">
          {connected ? (
            <Button
              variant="secondary"
              onClick={() => {
                disconnect.reset();
                setConfirming(true);
              }}
            >
              {portalText("operations", "disconnect_ed28e068")}</Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                onReconnect(widget.voiceAgentId);
              }}
            >
              {portalText("operations", "connect_again_93b38d41")}</Button>
          )}
        </div>
      ) : null}

      <Dialog
        open={confirming}
        title={portalText("operations", "disconnect_the_website_chat_for_value_e2154cf0", { v0: String(widget.voiceAgentName) })}
        onClose={() => {
          if (!disconnect.isPending) setConfirming(false);
        }}
        actions={
          <>
            <Button
              variant="ghost"
              disabled={disconnect.isPending}
              onClick={() => {
                setConfirming(false);
              }}
            >
              {portalText("operations", "keep_it_d9f36924")}</Button>
            <Button
              variant="primary"
              disabled={disconnect.isPending}
              onClick={() => {
                disconnect.mutate();
              }}
            >
              {disconnect.isPending ? portalText("operations", "disconnecting_801e5ad7") : portalText("operations", "disconnect_ed28e068")}
            </Button>
          </>
        }
      >
        <p className="c-empty-dek">
          {portalText("operations", "it_stops_answering_at_once_on_every_site_voice_is_turned_o_ff4316d9")}</p>
        {disconnect.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {apiErrorText(disconnect.error, portalText("operations", "it_could_not_be_disconnected_ca1bb7a9"))} {portalText("operations", "nothing_was_changed_c60d5d2a")}</p>
        ) : null}
      </Dialog>
    </article>
  );
}
